/**
 * A01 实时更新（spec-core §1.15 `admin.alerts`、`admin.todos`；spec-platform F.1、REQ-5.1-015、048）：
 * - 订阅后台 ws（/admin-api/v1/ws）：告警新增、重复、关闭 → 刷新告警与告警铃；队列计数变化、领取被释放 → 刷新队列与待办；
 * - 浏览器通知：首次进入 A01 时请求授权；收到新告警（含未知悉的重复推送）时调用 Notification API；拒绝授权后只用站内铃；
 * - 断线后按 1、2、4…30 秒重连；浏览器不支持 WebSocket / Notification 时静默降级为轮询（30 秒）。
 */
import { ADMIN_API_PREFIX } from '@nexora/shared';
import { useEffect, useRef } from 'react';

export interface WsMessage {
  ch: string;
  type: string;
  data: Record<string, unknown> | null;
}

/** 订阅后台 ws 频道；onMessage 收到 `{ ch, type, data }`。 */
export function useAdminChannels(channels: readonly string[], onMessage: (msg: WsMessage) => void): void {
  const handler = useRef(onMessage);
  handler.current = onMessage;
  const key = channels.join(',');
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.WebSocket !== 'function') return undefined;
    let socket: WebSocket | null = null;
    let stopped = false;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const connect = () => {
      const url = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}${ADMIN_API_PREFIX}/ws`;
      try {
        socket = new window.WebSocket(url);
      } catch {
        return;
      }
      socket.onopen = () => {
        retry = 0;
        for (const ch of key.split(',')) if (ch) socket?.send(JSON.stringify({ op: 'sub', ch }));
      };
      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(String(event.data)) as WsMessage;
          if (msg && typeof msg.ch === 'string') handler.current(msg);
        } catch {
          // 非 JSON 心跳等：忽略
        }
      };
      socket.onclose = () => {
        if (stopped) return;
        const wait = Math.min(30_000, 1_000 * 2 ** retry);
        retry += 1;
        timer = setTimeout(connect, wait);
      };
    };
    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      socket?.close();
    };
  }, [key]);
}

/** 浏览器通知是否可用且已授权。 */
export function notificationsGranted(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'granted';
}

/** 首次进入 A01 时请求浏览器通知授权（只在尚未选择时请求一次）。 */
export function useRequestNotificationPermission(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (window.Notification.permission !== 'default') return;
    try {
      void window.Notification.requestPermission();
    } catch {
      // 旧浏览器的回调式接口：忽略，只用站内铃
    }
  }, []);
}

/** 显示一条浏览器通知（未授权时不显示）。 */
export function showBrowserNotification(title: string, body: string, tag: string): void {
  if (!notificationsGranted()) return;
  try {
    new window.Notification(title, { body, tag });
  } catch {
    // 部分浏览器只允许在 Service Worker 中创建通知：忽略，站内铃照常提醒
  }
}
